import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const questionSetsAdapter = createEntityAdapter({
    sortComparer: (a, b) => (a.completed === b.completed) ? 0 : a.completed ? 1 : -1
})

const initialState = questionSetsAdapter.getInitialState()

export const questionSetsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getQuestionSets: builder.query({
            query: () => ({
                url: '/api/question-sets',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedQuestionSets = responseData.map(questionSet => {
                    questionSet.id = questionSet._id
                    return questionSet
                });
                return questionSetsAdapter.setAll(initialState, loadedQuestionSets)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'QuestionSet', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'QuestionSet', id }))
                    ]
                } else return [{ type: 'QuestionSet', id: 'LIST' }]
            }
        }),

        addNewQuestionSet: builder.mutation({
            query: initialQuestionSet => ({
                url: '/api/question-sets',
                method: 'POST',
                body: {
                    ...initialQuestionSet,
                }
            }),
            invalidatesTags: [
                { type: 'QuestionSet', id: "LIST" }
            ]
        }),

        updateQuestionSet: builder.mutation({
            query: initialQuestionSet => ({
                url: '/api/question-sets',
                method: 'PATCH',
                body: {
                    ...initialQuestionSet,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'QuestionSet', id: arg.id }
            ]
        }),
        deleteQuestionSet: builder.mutation({
            query: ({ id }) => ({
                url: `/api/question-sets`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'QuestionSet', id: arg.id }
            ]
        }),
        duplicateQuestionSet: builder.mutation({
            query: ({ id }) => ({
                url: `/api/question-sets/duplicate`,
                method: 'POST',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'QuestionSet', id: "LIST" }
            ]
        }),
        generateQuestionSetForUsers: builder.mutation({
            query: initialQuestionSet => ({
                url: '/api/question-sets/generate',
                method: 'POST',
                body: {
                    ...initialQuestionSet,
                }
            }),
            // invalidatesTags: [
            //     { type: 'QuestionSet', id: "LIST" }
            // ]
        }),

        getFinalQuestions: builder.query({
            query: ({ questionSetId }) => ({
                url: `/api/question-sets/${questionSetId}/final`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            providesTags: ['FinalQuestionSetList'],
        }),
        getFinalQuestionById: builder.query({
            query: ({ finalQuestionSetId }) => ({
                url: `/api/question-sets/final/${finalQuestionSetId}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            providesTags: (result, error, arg) => {
              return [{ type: 'FinalQuestionSetItem',  id: arg._id }]
            }
            //providesTags: ['FinalQuestionSetList'],
        }),
        updateFinalQuestionSet: builder.mutation({
            query: initialFinalQuestionSet => ({
                url: '/api/question-sets/final',
                method: 'PATCH',
                body: {
                    ...initialFinalQuestionSet,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'FinalQuestionSetList' },
                { type: 'FinalQuestionSetItem',  id: arg._id }
            ]
        }),

        deleteFinalQuestionSet: builder.mutation({
            query: ({ finalQuestionSetId }) => ({
                url: `/api/question-sets/final`,
                method: 'DELETE',
                body: { finalQuestionSetId }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'FinalQuestionSetList' }
            ]
        }),
    }),
})

export const {
    useGetQuestionSetsQuery,
    useAddNewQuestionSetMutation,
    useUpdateQuestionSetMutation,
    useDeleteQuestionSetMutation,
    useGenerateQuestionSetForUsersMutation,
    useGetFinalQuestionsQuery,
    useDeleteFinalQuestionSetMutation,
    useGetFinalQuestionByIdQuery,
    useUpdateFinalQuestionSetMutation,
    useDuplicateQuestionSetMutation
} = questionSetsApiSlice

// returns the query result object
export const selectQuestionSetsResult = questionSetsApiSlice.endpoints.getQuestionSets.select()

// creates memoized selector
const selectQuestionSetsData = createSelector(
    selectQuestionSetsResult,
    questionSetsResult => questionSetsResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllQuestionSets,
    selectById: selectQuestionSetById,
    selectIds: selectQuestionSetIds
    // Pass in a selector that returns the questionSets slice of state
} = questionSetsAdapter.getSelectors(state => selectQuestionSetsData(state) ?? initialState)